import React, { useEffect, useState, useMemo } from 'react'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  MoreHorizontal,
  Plus,
  Key,
  AlertTriangle,
  Trash2,
  Pencil,
} from 'lucide-react'
import { toast } from 'sonner'

import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Progress } from '../../components/ui/progress'
import { Checkbox } from '../../components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/ShadcnDropdownMenu'

import { createApiKey, listApiKeys, revokeApiKey, updateApiKey } from '../../services/apiKeys'
import type { ApiKey, CreateKeyResponse } from '../../services/apiKeys'

// ─── Constants ───────────────────────────────────────────────────

const ALL_SCOPES = [
  'chatbots:read',
  'chatbots:write',
  'training:write',
  'training:read',
  'analytics:read',
  'webhooks:read',
  'webhooks:write',
]

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  'chatbots:read': 'View chatbot details and list',
  'chatbots:write': 'Create, update, and delete chatbots',
  'training:write': 'Upload training data and manage sources',
  'training:read': 'View training data and sources',
  'analytics:read': 'Access usage statistics and analytics',
  'webhooks:read': 'View webhook subscriptions and delivery logs',
  'webhooks:write': 'Create, delete, and retry webhooks',
}

// ─── Helpers ─────────────────────────────────────────────────────

function maskKey(id: string): string {
  if (id.length <= 12) return id
  return `${id.slice(0, 8)}...${id.slice(-4)}`
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// ─── Stat Cards ──────────────────────────────────────────────────

function StatsCards({ keys }: { keys: ApiKey[] }) {
  const totalKeys = keys.length
  const activeKeys = keys.filter((k) => k.is_active).length
  const revokedKeys = totalKeys - activeKeys
  const recentlyUsed = keys.filter((k) => {
    if (!k.last_used_at) return false
    const diff = Date.now() - new Date(k.last_used_at).getTime()
    return diff < 7 * 24 * 60 * 60 * 1000 // 7 days
  }).length

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Plan usage card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">API Keys</CardTitle>
          <CardAction>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="text-3xl font-bold">{totalKeys}</div>
          <Progress value={totalKeys > 0 ? (activeKeys / totalKeys) * 100 : 0} className="h-2" />
          <div className="text-muted-foreground text-xs">{activeKeys} active of {totalKeys} total</div>
        </CardContent>
      </Card>

      {/* Active keys */}
      <Card>
        <CardHeader>
          <CardDescription>Active Keys</CardDescription>
          <CardAction>
            <Badge variant="success">{activeKeys}</Badge>
          </CardAction>
          <div className="text-3xl font-bold">{activeKeys}</div>
          <div className="text-muted-foreground text-xs">Currently enabled</div>
        </CardHeader>
      </Card>

      {/* Revoked keys */}
      <Card>
        <CardHeader>
          <CardDescription>Revoked Keys</CardDescription>
          <CardAction>
            {revokedKeys > 0 ? (
              <Badge variant="destructive">{revokedKeys}</Badge>
            ) : (
              <Badge variant="outline">0</Badge>
            )}
          </CardAction>
          <div className="text-3xl font-bold">{revokedKeys}</div>
          <div className="text-muted-foreground text-xs">No longer active</div>
        </CardHeader>
      </Card>

      {/* Recently used */}
      <Card>
        <CardHeader>
          <CardDescription>Active (7 days)</CardDescription>
          <CardAction>
            <Badge variant="info">{recentlyUsed}</Badge>
          </CardAction>
          <div className="text-3xl font-bold">{recentlyUsed}</div>
          <div className="text-muted-foreground text-xs">Used in the last 7 days</div>
        </CardHeader>
      </Card>
    </div>
  )
}

// ─── Create API Key Dialog ───────────────────────────────────────

function CreateApiKeyDialog({
  onCreated,
}: {
  onCreated: (res: CreateKeyResponse) => void
}) {
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [scopes, setScopes] = useState<string[]>(['chatbots:write', 'training:write'])
  const [loading, setLoading] = useState(false)

  function toggleScope(s: string) {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!label.trim() || scopes.length === 0) return
    try {
      setLoading(true)
      const res = await createApiKey(label.trim(), scopes)
      onCreated(res)
      setLabel('')
      setScopes(['chatbots:write', 'training:write'])
      setOpen(false)
      toast.success('API key created successfully')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create API key'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-1.5" />
          Create API Key
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New API Key</DialogTitle>
          <DialogDescription>
            Generate a scoped API key for server-to-server integrations. You'll only see the key once.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Key Name</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Production Server, iOS App"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Permissions</label>
            <div className="grid grid-cols-1 gap-2">
              {ALL_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    scopes.includes(scope)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Checkbox
                    checked={scopes.includes(scope)}
                    onCheckedChange={() => toggleScope(scope)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium font-mono">{scope}</div>
                    <div className="text-xs text-muted-foreground">{SCOPE_DESCRIPTIONS[scope]}</div>
                  </div>
                </label>
              ))}
            </div>
            {scopes.length === 0 && (
              <p className="text-xs text-destructive mt-2">Select at least one permission</p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || scopes.length === 0 || !label.trim()} loading={loading}>
              Create API Key
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── New Key Banner ──────────────────────────────────────────────

function NewKeyBanner({ keyData, onDismiss }: { keyData: CreateKeyResponse; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false)

  async function copyKey() {
    try {
      await navigator.clipboard.writeText(keyData.key)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // fallback
      const ta = document.createElement('textarea')
      ta.value = keyData.key
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
      <CardHeader>
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base text-amber-800 dark:text-amber-200">
              Your new API key is ready!
            </CardTitle>
            <CardDescription className="text-amber-700 dark:text-amber-300 mt-1">
              Copy this key now — it won't be shown again for security reasons.
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onDismiss} className="shrink-0 -mt-1">
            Dismiss
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <code className="flex-1 text-sm font-mono bg-background border rounded-lg px-3 py-2.5 break-all">
            {keyData.key}
          </code>
          <Button variant="outline" size="sm" onClick={copyKey} className="shrink-0">
            {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Scopes Cell (single-line, expandable) ───────────────────────

function ScopesCell({ scopes }: { scopes: string[] }) {
  const [open, setOpen] = useState(false)

  if (scopes.length === 0) {
    return <span className="text-muted-foreground text-xs italic">None</span>
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <Badge variant="info" size="sm" className="font-mono text-[10px]">
          {scopes[0]}
        </Badge>
        {scopes.length > 1 && (
          <span className="text-muted-foreground hover:text-foreground">
            +{scopes.length - 1} more
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-popover border rounded-md shadow-md p-2 space-y-1 min-w-[180px]">
          {scopes.map((scope) => (
            <Badge key={scope} variant="info" size="sm" className="font-mono text-[10px] block w-fit">
              {scope}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── DataTable ───────────────────────────────────────────────────

function ApiKeysDataTable({
  data,
  onRevoke,
}: {
  data: ApiKey[]
  onRevoke: (id: string, label: string) => void
}) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [rowSelection, setRowSelection] = useState({})

  const columns = useMemo<ColumnDef<ApiKey>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'label',
        header: ({ column }) => (
          <Button
            className="-ml-3"
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <ArrowUpDown className="ml-2 h-3.5 w-3.5" />
          </Button>
        ),
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.getValue('label')}</div>
            <div className="text-xs text-muted-foreground font-mono">{maskKey(row.original.id)}</div>
          </div>
        ),
      },
      {
        accessorKey: 'scopes',
        header: 'Permissions',
        cell: ({ row }) => <ScopesCell scopes={row.original.scopes} />,
        enableSorting: false,
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => (
          <Button
            className="-ml-3"
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Created
            <ArrowUpDown className="ml-2 h-3.5 w-3.5" />
          </Button>
        ),
        cell: ({ row }) => formatDate(row.getValue('created_at')),
      },
      {
        accessorKey: 'last_used_at',
        header: ({ column }) => (
          <Button
            className="-ml-3"
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Last Used
            <ArrowUpDown className="ml-2 h-3.5 w-3.5" />
          </Button>
        ),
        cell: ({ row }) => {
          const val = row.getValue('last_used_at') as string | null
          return val ? formatDate(val) : (
            <span className="text-muted-foreground italic">Never</span>
          )
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ row }) => {
          const active = row.original.is_active
          return (
            <Badge variant={active ? 'success' : 'destructive'} className="capitalize">
              {active ? 'Active' : 'Revoked'}
            </Badge>
          )
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => {
          const key = row.original
          return (
            <div className="text-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      onClick={() => {
                        navigator.clipboard.writeText(key.id)
                        toast.success('Key ID copied')
                      }}
                    >
                      <Copy className="h-4 w-4" />
                      Copy Key ID
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        const newLabel = prompt('Rename API key:', key.label)
                        if (newLabel && newLabel !== key.label) {
                          try {
                            await updateApiKey(key.id, { label: newLabel })
                            toast.success('Key renamed')
                            window.location.reload()
                          } catch {
                            toast.error('Failed to rename key')
                          }
                        }
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                      Rename
                    </DropdownMenuItem>
                    {key.is_active && (
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => onRevoke(key.id, key.label)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Revoke
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [onRevoke]
  )

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    state: { sorting, columnFilters, rowSelection },
  })

  return (
    <div className="space-y-4">
      {/* Toolbar: filter + create */}
      <div className="flex items-center justify-between gap-3">
        <Input
          placeholder="Filter API keys..."
          value={(table.getColumn('label')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('label')?.setFilterValue(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {/* Table */}
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Key className="h-8 w-8 opacity-30" />
                      <p className="font-medium">No API keys yet</p>
                      <p className="text-xs">Create your first API key to get started</p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-end space-x-2">
        <div className="text-muted-foreground flex-1 text-sm">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────

export default function ApiKeysPage() {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [newKey, setNewKey] = useState<CreateKeyResponse | null>(null)

  async function load() {
    try {
      setLoading(true)
      const data = await listApiKeys()
      setKeys(data)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load API keys'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function handleRevoke(id: string, label: string) {
    if (!confirm(`Revoke "${label}"? This immediately disables all applications using this key.`)) return
    try {
      await revokeApiKey(id)
      toast.success('API key revoked')
      await load()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to revoke key'
      toast.error(msg)
    }
  }

  function handleCreated(res: CreateKeyResponse) {
    setNewKey(res)
    load()
  }

  if (loading && keys.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">API Keys Management</h1>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 w-24 bg-muted rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">API Keys Management</h1>
        <CreateApiKeyDialog onCreated={handleCreated} />
      </div>

      {/* Stat Cards */}
      <StatsCards keys={keys} />

      {/* New Key Banner */}
      {newKey && <NewKeyBanner keyData={newKey} onDismiss={() => setNewKey(null)} />}

      {/* DataTable */}
      <ApiKeysDataTable data={keys} onRevoke={handleRevoke} />
    </div>
  )
}
